import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchBenchmarkRun,
  fetchBenchmarkRunResults,
  BenchmarkResult,
} from "@/lib/api/resources/benchmark";
import { Content } from "@/components/layout/content";
import { Loading } from "@/components/common/loading";
import { Tabs, Button } from "antd";
import { RunAdvice } from "../components/RunAdvice";
import {
  BarChartOutlined,
  CodeOutlined,
  UnorderedListOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { BenchmarkConfigDetails } from "../components/BenchmarkConfigSummary";
import { RunInsights } from "../components/RunInsights";
import { RunExecutionLog } from "../components/RunExecutionLog";
import { RunCasesTable } from "../components/RunCasesTable";
import { PromoteBenchmarkRunModal } from "../components/PromoteBenchmarkRunModal";

const collectionToArray = (data: any): BenchmarkResult[] =>
  Array.isArray(data) ? data : data?.member || data?.["hydra:member"] || [];

export default function BenchmarkRunDetail() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("insights");
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);

  const { data: run } = useQuery({
    queryKey: ["benchmark_run", id],
    queryFn: () => fetchBenchmarkRun(id as string),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = (query.state?.data as any)?.status;
      return status === "completed" || status === "failed" ? false : 5000;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["benchmark_results", id],
    queryFn: () => fetchBenchmarkRunResults(id as string),
    enabled: !!id,
    refetchInterval: () =>
      run?.status === "completed" || run?.status === "failed" ? false : 5000,
  });

  const results = collectionToArray(data);
  const isRunning = run?.status === "running" || run?.status === "pending";

  return (
    <>
      <Content>
        {run && (
          <div className="mb-6 overflow-hidden rounded-xl bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-700 p-6 text-white relative">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <svg
                  width="120"
                  height="120"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19,3H5C3.89,3 3,3.89 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19M17,17H7V15H17V17M17,13H7V11H17V13M17,9H7V7H17V9Z" />
                </svg>
              </div>
              <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  {/* Page context title brought inside the banner to replace the redundant white page Topbar */}
                  <div className="text-xs font-semibold uppercase tracking-wider text-blue-100/80 mb-1">
                    Benchmark Run Details
                  </div>
                  <h2 className="text-2xl font-bold mb-2 tracking-tight">
                    {run.benchmarkConfig?.name ||
                      run.agent?.name ||
                      "Benchmark Execution Report"}
                  </h2>
                  <div className="flex items-center gap-3 opacity-90 text-sm font-medium">
                    <span
                      className="font-mono bg-white/20 px-2 py-0.5 rounded text-white"
                      title="Run ID"
                    >
                      {run.id?.split("/").pop()?.substring(0, 8)}...
                    </span>
                    <span>•</span>
                    <span>
                      {new Date(run.createdAt || Date.now()).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <div
                    className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest shadow-sm ${
                      isRunning
                        ? "bg-blue-400 text-blue-950 animate-pulse"
                        : run.status === "completed"
                          ? "bg-emerald-400 text-emerald-950"
                          : run.status === "failed"
                            ? "bg-red-400 text-red-950"
                            : "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {run.status}
                  </div>
                  <div className="mt-2 text-sm font-medium opacity-90 bg-black/20 px-3 py-1 rounded-full">
                    {run.completedCases} / {run.totalCases} Cases Completed
                  </div>
                  {run.status === "completed" &&
                    (run.config?.input?.type === "image_folder" ||
                      run.config?.input?.type === "eml_folder") && (
                      <Button
                        size="small"
                        type="primary"
                        className="mt-2 bg-amber-500 hover:bg-amber-400 border-amber-500 text-white shadow-sm"
                        onClick={() => setIsPromoteModalOpen(true)}
                      >
                        Promote to Ground Truth
                      </Button>
                    )}
                  <Link
                    to={`/benchmark/compare?runs=${run.id?.split("/").pop()}`}
                    className="mt-2 text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-colors"
                    title="Compare this run against another, case by case"
                  >
                    ⇄ Compare with another run
                  </Link>
                </div>
              </div>
            </div>

            <PromoteBenchmarkRunModal
              runId={id as string}
              results={results}
              isOpen={isPromoteModalOpen}
              onClose={() => setIsPromoteModalOpen(false)}
            />

            <div className="p-6">
              {run.status === "failed" && run.statusMessage && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-r flex items-start shadow-sm">
                  <svg
                    className="w-5 h-5 mr-3 text-red-500 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h4 className="font-bold">Execution Failed</h4>
                    <p className="mt-1 text-sm opacity-90">
                      {run.statusMessage}
                    </p>
                  </div>
                </div>
              )}

              {/* Configuration Section */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Configuration Parameters
                </h3>
                <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100">
                  <BenchmarkConfigDetails config={run.config} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 overflow-hidden">
          <div className="px-6 pt-4">
            {isLoading && !run ? (
              <div className="p-12">
                <Loading />
              </div>
            ) : run ? (
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: "insights",
                    label: (
                      <span>
                        <BarChartOutlined /> Summary & Insights
                      </span>
                    ),
                    children: (
                      <div className="pb-6">
                        <RunInsights run={run} results={results} />
                      </div>
                    ),
                  },
                  {
                    key: "log",
                    label: (
                      <span>
                        <CodeOutlined /> Execution Log
                      </span>
                    ),
                    children: (
                      <div className="pb-6">
                        <RunExecutionLog run={run} results={results} />
                      </div>
                    ),
                  },
                  {
                    key: "cases",
                    label: (
                      <span>
                        <UnorderedListOutlined /> Test Cases ({results.length})
                      </span>
                    ),
                    children: (
                      <div className="pb-6">
                        <RunCasesTable
                          results={results}
                          loading={isLoading && results.length === 0}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "advice",
                    label: (
                      <span>
                        <RobotOutlined /> Judge Advice
                      </span>
                    ),
                    children: (
                      <div className="pb-6">
                        <RunAdvice run={run} />
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <div className="p-12">
                <Loading />
              </div>
            )}
          </div>
        </div>
      </Content>
    </>
  );
}
